/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Button } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import defaultPaymentConnectorDropinStyle from '../payment-instrument.dropin.style';
import defaultPaymentConnectorPaypalStyle from '../payment-instrument.paypal.style';
import defaultPaymentConnectorStyle from '../payment-instrument.style';
import Iframe from './iframe';

export interface IAddPaymentFormProps {
    acceptPageUrl?: string;
    acceptPageContent?: string;
    iframeRef?: React.RefObject<Iframe>;
    canSubmit?: boolean;
    canCancel?: boolean;
    hasSelectedItem?: boolean;
    iFrameHeightOverride?: number;
    style?: string;
    requestUrlOrigin?: string;
    messageOrigin?: string;
    isFetchingPaymentConnector?: boolean;
    paymentStyleOverride?: string;
    resources: {
        cancelButtonText: string;
        submitButtonText: string;
        iframeAriaLabel: string;
    };
    onIFrameMessage?(event: MessageEvent): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 * AddPaymentForm SFC.
 * @param root0
 * @param root0.acceptPageUrl
 * @param root0.acceptPageContent
 * @param root0.onSubmit
 * @param root0.onCancel
 * @param root0.onIFrameMessage
 * @param root0.iframeRef
 * @param root0.canSubmit
 * @param root0.canCancel
 * @param root0.iFrameHeightOverride
 * @param root0.requestUrlOrigin
 * @param root0.messageOrigin
 * @param root0.isFetchingPaymentConnector
 * @param root0.paymentStyleOverride
 * @param root0.resources
 * @param root0.resources.cancelButtonText
 * @param root0.resources.submitButtonText
 * @param root0.resources.iframeAriaLabel
 * @extends {React.SFC<IAddPaymentFormProps>}
 */
const AddPaymentForm: React.SFC<IAddPaymentFormProps> = ({
    acceptPageUrl = '',
    acceptPageContent = '',
    onSubmit,
    onCancel,
    onIFrameMessage,
    iframeRef,
    canSubmit,
    canCancel,
    iFrameHeightOverride,
    requestUrlOrigin,
    messageOrigin,
    isFetchingPaymentConnector,
    paymentStyleOverride,
    resources: { cancelButtonText, submitButtonText, iframeAriaLabel }
}) => (
    <div className={classnames('ms-checkout-payment-instrument__add', { 'is-fetching': isFetchingPaymentConnector })}>
        <Iframe
            className='ms-checkout-payment-instrument__add'
            ref={iframeRef}
            iframeAriaLabel={iframeAriaLabel}
            displayContent={acceptPageContent}
            sourceUrl={acceptPageUrl}
            requestUrlOrigin={requestUrlOrigin}
            messageOrigin={messageOrigin}
            onIFrameMessage={onIFrameMessage}
            height={iFrameHeightOverride}
            css={paymentStyleOverride || (defaultPaymentConnectorStyle + defaultPaymentConnectorDropinStyle + defaultPaymentConnectorPaypalStyle)}
        />
        {canSubmit && (
            <Button
                className='ms-checkout-payment-instrument__btn-save'
                title={submitButtonText}
                color='primary'
                onClick={onSubmit}
            >
                {submitButtonText}
            </Button>
        )}
        {canCancel && (
            <Button
                className='ms-checkout-payment-instrument__btn-cancel'
                title={cancelButtonText}
                color='secondary'
                onClick={onCancel}
            >
                {cancelButtonText}
            </Button>
        )}
    </div>
);

export default React.memo(AddPaymentForm);
